import formStyle from '@/styles/modules/form.module.scss';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useController } from 'react-hook-form';
import type { DeepMap, FieldError, FieldValues, UseControllerProps } from 'react-hook-form/dist/types';
import FormLabel from '../FormLabel/FormLabel';
import styles from './FormTextArea.module.scss';

type Props<T extends FieldValues> = {
  label?: string;
  placeholder?: string;
  required?: boolean;
  readOnly?: boolean;
  rows?: number;
  prefix?: React.ReactNode;
  outlined?: boolean;
  onChange?: () => void;
  disabled?: boolean;
} & UseControllerProps<T>;

function FormTextArea<T extends FieldValues>({
  name,
  label,
  placeholder,
  required = false,
  readOnly = false,
  rows = 5,
  control,
  prefix,
  outlined = false,
  onChange,
  disabled = false,
}: Props<T>) {
  const {
    field,
    fieldState: { error },
  } = useController<T>({ name, control });
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [height, setHeight] = useState(200);

  useEffect(() => {
    if (textAreaRef.current) {
      const scrollHeight = textAreaRef.current.scrollHeight;
      //下限200px、上限500px
      if (scrollHeight < 200) {
        setHeight(200);
      } else if (scrollHeight > 500) {
        setHeight(500);
      } else if (height < scrollHeight) {
        // 伸びる方向にのみ高さを変更する
        setHeight(scrollHeight);
      }
    }
  }, [field.value]);

  return (
    <div className={formStyle.formComponentStyle}>
      {/* ラベル */}
      {label && (
        <FormLabel>
          {label}
          {required && <span className={formStyle.required}>*</span>}
        </FormLabel>
      )}
      {/* フィールドブロック */}
      <div className={error && formStyle.hasError}>
        <div className={`${styles.wrapper} ${outlined && styles.outlined}`}>
          {prefix && <div className={formStyle.prefix}>{prefix}</div>}
          <textarea
            ref={textAreaRef}
            readOnly={readOnly}
            required={required}
            name={field.name}
            placeholder={placeholder}
            rows={rows}
            className={styles.input}
            onChange={(e) => {
              field.onChange(e);
              if (onChange) onChange();
            }}
            style={{ height: height ? `${height}px` : 'auto' }}
            value={field.value}
            disabled={disabled}
          />
        </div>
        {/* エラー */}
        {error && <p className={formStyle.validateErrors}>{(error as DeepMap<FieldValues, FieldError>).message}</p>}
      </div>
    </div>
  );
}
export default FormTextArea;
